import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import { addDoc, collection, doc, getDoc, onSnapshot, runTransaction, setDoc } from "firebase/firestore";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import BuildingAutocomplete from "./components/BuildingAutocomplete";
import { db } from "../../firebase/firestore";
import {
  calculateCheckoutTotals,
  formatCurrency,
  getDeliveryScheduleBounds,
  getStorageJSON,
  loadRazorpayScript,
  notifyOrderSuccess,
  resolveScheduledDeliveryDate,
  normalizeDeliveryArea,
  sanitizeText,
  SERVICEABLE_AREAS,
  toDateInputValue,
  verifyPaymentResponse,
} from "../../utils/helpers";
import { auth } from "../../firebase/auth";

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

function normalizeCouponCode(input) {
  return String(input || "")
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, "")
    .slice(0, 24);
}

function buildCouponUsageKey(offerId, userIdOrEmail) {
  const safeOfferId = String(offerId || "").replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 64);
  const safeUser = String(userIdOrEmail || "guest")
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "_")
    .slice(0, 80);
  return `${safeOfferId}_${safeUser}`;
}

function toPositiveInt(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return Math.floor(parsed);
}

function parseCouponNumber(value, fallback = 0) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const normalized = String(value ?? "")
    .replace(/,/g, "")
    .replace(/[^\d.-]/g, "")
    .trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function inferPercentFromCode(code) {
  const match = String(code || "").match(/(\d{1,2})/);
  const value = Number(match?.[1] || 0);
  return Number.isFinite(value) && value > 0 && value <= 80 ? value : 0;
}

function getCouponDiscountMeta(coupon) {
  const offerType = String(coupon?.offerType || "").toLowerCase();
  const discountPct = parseCouponNumber(
    coupon?.discount ?? coupon?.discountPercent ?? coupon?.percentOff ?? coupon?.percentage,
    0
  );
  const flatDiscount = parseCouponNumber(
    coupon?.flatDiscount ?? coupon?.amountOff ?? coupon?.value ?? coupon?.discountAmount,
    0
  );

  return {
    offerType,
    discountPct: offerType === "flat" ? 0 : (discountPct > 0 ? discountPct : inferPercentFromCode(coupon?.code)),
    flatDiscount: offerType === "percentage" ? 0 : flatDiscount,
    freeDelivery: offerType === "free_delivery" || isFreeDeliveryCoupon(coupon),
  };
}

function normalizeCouponCategory(coupon) {
  const raw = String(coupon?.appliesToCategory || coupon?.category || coupon?.targetCategory || "all")
    .trim()
    .toLowerCase();
  if (["combo", "snacks", "sweets", "all"].includes(raw)) return raw;
  return "all";
}

function itemLineTotal(item) {
  return (Number(item?.price || 0) || 0) * (Number(item?.qty || 0) || 0);
}

function getCouponEligibleSubtotal(cartItems, coupon) {
  const target = normalizeCouponCategory(coupon);
  const list = Array.isArray(cartItems) ? cartItems : [];

  if (target === "all") {
    return list.reduce((sum, item) => sum + itemLineTotal(item), 0);
  }

  return list.reduce((sum, item) => {
    const category = String(item?.category || "").toLowerCase();
    return category === target ? sum + itemLineTotal(item) : sum;
  }, 0);
}

function hasCouponEligibleItems(cartItems, coupon) {
  return getCouponEligibleSubtotal(cartItems, coupon) > 0;
}

function isFreeDeliveryCoupon(coupon) {
  const type = String(coupon?.discountType || coupon?.type || "").toLowerCase();
  const code = String(coupon?.code || "").toUpperCase();
  return Boolean(
    coupon?.freeDelivery ||
    coupon?.isFreeDelivery ||
    coupon?.deliveryFree ||
    type === "free_delivery" ||
    type === "free-shipping" ||
    type === "freeshipping" ||
    code.includes("FREEDEL") ||
    code.includes("FREEDELIVERY")
  );
}

function generateInvoicePdf(order, customerDetails) {
  const pdf = new jsPDF();
  const left = 14;
  const right = 196;

  pdf.setFillColor(249, 115, 22);
  pdf.rect(0, 0, 210, 28, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(18);
  pdf.text("Shreeji Surti Swad", left, 16);
  pdf.setFontSize(12);
  pdf.text("TAX INVOICE", right, 16, { align: "right" });

  pdf.setTextColor(23, 23, 23);
  pdf.setDrawColor(229, 231, 235);
  pdf.roundedRect(left, 34, 182, 48, 3, 3, "S");

  pdf.setFontSize(11);
  pdf.text(`Invoice No: INV-${order.id}`, left + 4, 44);
  pdf.text(`Order ID: ${order.id}`, left + 4, 51);
  pdf.text(`Order Date: ${new Date(order.createdAt).toLocaleString()}`, left + 4, 58);
  pdf.text(`Delivery Area: ${order.location}`, left + 4, 65);
  pdf.text(`ETA: ${new Date(order.deliveryDate).toLocaleDateString()}`, left + 4, 72);

  pdf.text(`Customer: ${customerDetails.name}`, 120, 44);
  pdf.text(`Email: ${customerDetails.email}`, 120, 51);
  pdf.text(`Mobile: ${customerDetails.phone || "N/A"}`, 120, 58);
  pdf.text(`PIN: ${order.address?.pinCode || "N/A"}`, 120, 65);

  let y = 92;
  pdf.setFillColor(245, 245, 245);
  pdf.rect(left, y - 6, 182, 8, "F");
  pdf.setFontSize(10);
  pdf.text("Item", left + 2, y - 1);
  pdf.text("Qty", 130, y - 1);
  pdf.text("Amount", right - 2, y - 1, { align: "right" });
  y += 8;

  order.items.forEach((item, index) => {
    const line = `${index + 1}. ${item.name} (${item.size === "1000" ? "1kg" : `${item.size}g`})`;
    const amount = Number(item.price || 0) * Number(item.qty || 0);
    pdf.text(line, left + 2, y);
    pdf.text(String(item.qty || 0), 130, y);
    pdf.text(formatCurrency(amount), right - 2, y, { align: "right" });
    y += 7;
  });

  y += 4;
  pdf.line(left, y, right, y);
  y += 8;
  pdf.text(`Subtotal: ${formatCurrency(order.pricing.subtotal)}`, 128, y);
  y += 7;
  pdf.text(`GST (5%): ${formatCurrency(order.pricing.gst)}`, 128, y);
  y += 7;
  pdf.text(`Delivery: ${formatCurrency(order.pricing.delivery)}`, 128, y);
  y += 7;
  if (order.coupon?.discount) {
    pdf.text(`Coupon (${order.coupon.code}): -${formatCurrency(order.coupon.discount)}`, 128, y);
    y += 7;
  }
  pdf.setFontSize(13);
  pdf.setTextColor(249, 115, 22);
  pdf.text(`Grand Total: ${formatCurrency(order.payableTotal ?? order.pricing.total)}`, 128, y);
  y += 10;

  pdf.setTextColor(23, 23, 23);
  pdf.setFontSize(11);
  pdf.text(`Payment ID: ${order.payment.paymentId}`, left, y);
  y += 6;
  pdf.text(`Status: ${order.payment.verified ? "Verified" : "Pending Verification"}`, left, y);
  y += 8;
  pdf.setFontSize(10);
  pdf.setTextColor(110, 110, 110);
  pdf.text("Terms: Orders before 10 PM are delivered next day. Post 10 PM orders are delivered within 48 hours.", left, y);

  pdf.save(`invoice_${order.id}.pdf`);
}

export default function Cart() {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [location, setLocation] = useState("Malad");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [address, setAddress] = useState({
    flatNo: "",
    buildingNumber: "",
    landmark: "",
    road: "",
    pinCode: "",
  });
  const [paymentStatus, setPaymentStatus] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [couponMessage, setCouponMessage] = useState("");
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [deliveryDateInput, setDeliveryDateInput] = useState("");
  const [platformConfig, setPlatformConfig] = useState({
    websiteClosed: false,
    websiteCloseMessage: "",
    acceptOrders: true,
    codEnabled: true,
    codMaintenanceMessage: "Cash on Delivery is temporarily unavailable.",
  });

  const getFriendlyCheckoutError = (error) => {
    const code = String(error?.code || "").toLowerCase();
    if (code.includes("permission-denied") || code.includes("insufficient")) {
      return "We could not save your order right now. Please refresh and try again.";
    }
    if (code.includes("unauthenticated")) {
      return "Please login again to complete payment.";
    }
    if (code.includes("unavailable")) {
      return "Server is temporarily busy. Please try again in a moment.";
    }
    return "We could not complete your order right now. Please try again.";
  };


  useEffect(() => {
    const syncCart = async () => {
      const cartData = getStorageJSON("cart", []);
      let processedCart = Array.isArray(cartData) ? cartData : [];

      // 🔥 FETCH CART ITEM DETAILS FROM FIREBASE
      if (processedCart.length > 0 && processedCart[0].id) {
        try {
          const enrichedCart = await Promise.all(
            processedCart.map(async (cartItem) => {
              try {
                const itemDoc = await getDoc(doc(db, "items", cartItem.id));
                if (itemDoc.exists()) {
                  const fbData = itemDoc.data();
                  return {
                    ...cartItem,
                    name: fbData.name || cartItem.name,
                    price: fbData.price || cartItem.price,
                    image: fbData.image || cartItem.image,
                    category: fbData.category || cartItem.category,
                    description: fbData.description || cartItem.description,
                  };
                }
              } catch (error) {
                // Fallback to cart item data
              }
              return cartItem;
            })
          );
          processedCart = enrichedCart;
        } catch (error) {
          // Fallback to local cart data
        }
      }

      setCart(processedCart);

      const user = getStorageJSON("user", null);
      setLocation(normalizeDeliveryArea(user?.location));
      setCustomerName(user?.name || "Guest User");
      setCustomerEmail(user?.email || "");
      setCustomerPhone(user?.phone || "");
      setAddress({
        flatNo: user?.addressDetails?.flatNo || "",
        buildingNumber: user?.addressDetails?.buildingNumber || "",
        landmark: user?.addressDetails?.landmark || "",
        road: user?.addressDetails?.road || "",
        pinCode: user?.addressDetails?.pinCode || "",
      });

      if (user?.uid) {
        try {
          const userSnap = await getDoc(doc(db, "users", user.uid));
          if (userSnap.exists()) {
            const userData = userSnap.data();
            setCustomerName(userData.name || user?.name || "Guest User");
            setCustomerEmail(userData.email || user?.email || "");
            setCustomerPhone(userData.phone || user?.phone || "");
            setLocation(normalizeDeliveryArea(userData.location || user?.location));

            setAddress({
              flatNo: userData.addressDetails?.flatNo || "",
              buildingNumber: userData.addressDetails?.buildingNumber || "",
              landmark: userData.addressDetails?.landmark || "",
              road: userData.addressDetails?.road || "",
              pinCode: userData.addressDetails?.pinCode || "",
            });
          }
        } catch {
          // no-op fallback to local session values
        }
      }
    };

    syncCart();
    window.addEventListener("storage", syncCart);
    window.addEventListener("cart-updated", syncCart);

    return () => {
      window.removeEventListener("storage", syncCart);
      window.removeEventListener("cart-updated", syncCart);
    };
  }, []);

  const pricing = useMemo(() => calculateCheckoutTotals(cart, location), [cart, location]);

  const couponDiscount = useMemo(() => {
    if (!appliedCoupon) return 0;
    const meta = getCouponDiscountMeta(appliedCoupon);
    const eligibleSubtotal = getCouponEligibleSubtotal(cart, appliedCoupon);
    if (eligibleSubtotal <= 0) return 0;
    const discountPct = meta.discountPct;
    const rawDiscountFromPercent = (eligibleSubtotal * discountPct) / 100;
    const maxDiscount = parseCouponNumber(appliedCoupon.maxDiscount, 0);
    const cappedPercent = maxDiscount > 0 ? Math.min(rawDiscountFromPercent, maxDiscount) : rawDiscountFromPercent;
    const flatDiscount = meta.flatDiscount;
    const bestDiscount = Math.max(cappedPercent, flatDiscount);
    return +bestDiscount.toFixed(2);
  }, [appliedCoupon, cart]);

  const deliveryCouponDiscount = useMemo(() => {
    if (!appliedCoupon) return 0;
    return getCouponDiscountMeta(appliedCoupon).freeDelivery ? Number(pricing.delivery || 0) : 0;
  }, [appliedCoupon, pricing.delivery]);

  const totalCouponDiscount = useMemo(() => {
    return +(couponDiscount + deliveryCouponDiscount).toFixed(2);
  }, [couponDiscount, deliveryCouponDiscount]);

  const totalPayable = useMemo(() => {
    return +Math.max(0, pricing.total - totalCouponDiscount).toFixed(2);
  }, [pricing.total, totalCouponDiscount]);

  const scheduleBounds = useMemo(() => getDeliveryScheduleBounds(new Date()), []);

  useEffect(() => {
    if (!deliveryDateInput) {
      setDeliveryDateInput(toDateInputValue(scheduleBounds.minDate));
    }
  }, [deliveryDateInput, scheduleBounds.minDate]);

  useEffect(() => {
    const unsubscribeOffers = onSnapshot(
      collection(db, "offers"),
      (snap) => {
        const now = new Date();
        const active = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((offer) => offer.active !== false)
          .filter((offer) => !offer.expiry || new Date(offer.expiry) >= now)
          .sort((a, b) => Number(b.createdAtMs || 0) - Number(a.createdAtMs || 0));
        setAvailableCoupons(active);
      },
      () => {
        setAvailableCoupons([]);
      }
    );

    const unsubscribePlatform = onSnapshot(
      doc(db, "appConfig", "platform"),
      (snap) => {
        if (!snap.exists()) return;
        setPlatformConfig((prev) => ({ ...prev, ...snap.data() }));
      },
      () => {
        // Keep defaults when read is denied or temporarily unavailable.
      }
    );

    return () => {
      unsubscribeOffers();
      unsubscribePlatform();
    };
  }, []);

  useEffect(() => {
    if (!platformConfig.codEnabled && paymentMethod === "cod") {
      setPaymentMethod("upi");
    }
  }, [platformConfig.codEnabled, paymentMethod]);

  const applyCoupon = async () => {
    setCouponMessage("");
    const code = normalizeCouponCode(couponInput);
    const user = getStorageJSON("user", null);
    const userKey = user?.uid || customerEmail.trim().toLowerCase() || "guest";

    if (!code) {
      setCouponMessage("Enter a valid coupon code first.");
      return;
    }

    if (appliedCoupon && String(appliedCoupon.code || "").toUpperCase() === code) {
      setCouponMessage("This coupon is already applied.");
      return;
    }

    const found = availableCoupons.find((coupon) => String(coupon.code || "").toUpperCase() === code);
    if (!found) {
      setAppliedCoupon(null);
      setCouponMessage("Invalid or expired coupon.");
      return;
    }

    const minOrder = Number(found.minOrder || 0);
    const eligibleSubtotal = getCouponEligibleSubtotal(cart, found);

    if (!hasCouponEligibleItems(cart, found) && normalizeCouponCategory(found) !== "all") {
      setAppliedCoupon(null);
      setCouponMessage(`This coupon works only on ${normalizeCouponCategory(found)} items.`);
      return;
    }

    if (eligibleSubtotal < minOrder) {
      setAppliedCoupon(null);
      setCouponMessage(`Coupon needs minimum order of ${formatCurrency(minOrder)}.`);
      return;
    }

    const usageLimit = toPositiveInt(found.usageLimit, 0);
    const usedCount = toPositiveInt(found.usedCount, 0);
    if (usageLimit > 0 && usedCount >= usageLimit) {
      setAppliedCoupon(null);
      setCouponMessage("This coupon usage limit is reached.");
      return;
    }

    if (found.id) {
      try {
        const usageSnap = await getDoc(doc(db, "offerUsage", buildCouponUsageKey(found.id, userKey)));
        const usedByUser = toPositiveInt(usageSnap.data()?.usedCount, 0);
        const perUserLimit = toPositiveInt(found.perUserLimit, 1) || 1;
        if (usedByUser >= perUserLimit) {
          setAppliedCoupon(null);
          setCouponMessage(`This coupon can be used only ${perUserLimit} time(s) per account.`);
          return;
        }
      } catch {
        // Ignore per-user usage check failures and continue with optimistic apply.
      }
    }

    setAppliedCoupon(found);
    setCouponMessage(`Coupon ${found.code} applied successfully.`);
  };

  const saveCart = (nextCart) => {
    setCart(nextCart);
    localStorage.setItem("cart", JSON.stringify(nextCart));
    window.dispatchEvent(new Event("cart-updated"));
  };

  const updateQty = (index, nextQty) => {
    const numericQty = Number(nextQty);
    if (!Number.isFinite(numericQty)) return;

    const nextCart = [...cart];
    if (numericQty <= 0) {
      nextCart.splice(index, 1);
    } else {
      nextCart[index] = { ...nextCart[index], qty: numericQty };
    }

    saveCart(nextCart);
  };

  const placeOrder = async () => {
    if (!cart.length || placingOrder) return;

    setPaymentStatus("");

    const cleanName = sanitizeText(customerName, 60) || "Guest User";
    const cleanEmail = customerEmail.trim().toLowerCase();
    const cleanPhone = customerPhone.trim();
    const cleanAddress = {
      flatNo: sanitizeText(address.flatNo, 40),
      buildingNumber: sanitizeText(address.buildingNumber, 60),
      landmark: sanitizeText(address.landmark, 80),
      road: sanitizeText(address.road, 80),
      pinCode: sanitizeText(address.pinCode, 10),
    };

    if (!cleanAddress.flatNo || !cleanAddress.road || !/^\d{6}$/.test(cleanAddress.pinCode)) {
      setPaymentStatus("Please enter full address details with a valid 6-digit pin code.");
      return;
    }

    if (!/^\d{10}$/.test(cleanPhone)) {
      setPaymentStatus("Please enter a valid 10-digit mobile number.");
      return;
    }

    if (platformConfig.websiteClosed || platformConfig.acceptOrders === false) {
      setPaymentStatus(platformConfig.websiteCloseMessage || "Orders are temporarily disabled right now.");
      return;
    }

    if (paymentMethod === "cod" && !platformConfig.codEnabled) {
      setPaymentStatus(platformConfig.codMaintenanceMessage || "Cash on Delivery is currently unavailable.");
      return;
    }

    if (paymentMethod === "upi" && !RAZORPAY_KEY) {
      setPaymentStatus("Payment setup is incomplete. Please contact support.");
      return;
    }

    const user = getStorageJSON("user", null);
    const authUser = auth.currentUser;

    if (!user?.uid || !authUser?.uid) {
      setPaymentStatus("Please login first to place your order.");
      return;
    }

    if (user.uid !== authUser.uid) {
      setPaymentStatus("Please wait a moment and try again. Session is syncing.");
      return;
    }

    const validateAppliedCoupon = async () => {
      if (!appliedCoupon?.id) {
        return { ok: true };
      }

      const userKey = user.uid;
      const offerRef = doc(db, "offers", appliedCoupon.id);
      const usageRef = doc(db, "offerUsage", buildCouponUsageKey(appliedCoupon.id, userKey));
      const [offerSnap, usageSnap] = await Promise.all([getDoc(offerRef), getDoc(usageRef)]);

      if (!offerSnap.exists()) {
        return {
          ok: false,
          message: "Selected coupon is no longer available.",
          clearCoupon: true,
        };
      }

      const offerData = offerSnap.data() || {};
      const offerCode = String(offerData.code || "").toUpperCase();
      const inputCode = String(appliedCoupon.code || "").toUpperCase();

      if (offerData.active === false || (offerCode && offerCode !== inputCode)) {
        return {
          ok: false,
          message: "This coupon is inactive. Please apply another coupon.",
          clearCoupon: true,
        };
      }

      if (offerData.expiry && new Date(offerData.expiry) < new Date()) {
        return {
          ok: false,
          message: "This coupon is expired.",
          clearCoupon: true,
        };
      }

      const minOrder = parseCouponNumber(offerData.minOrder, 0);
      const eligibleSubtotal = getCouponEligibleSubtotal(cart, offerData);

      if (!hasCouponEligibleItems(cart, offerData) && normalizeCouponCategory(offerData) !== "all") {
        return {
          ok: false,
          message: `This coupon works only on ${normalizeCouponCategory(offerData)} items.`,
          clearCoupon: true,
        };
      }

      if (eligibleSubtotal < minOrder) {
        return {
          ok: false,
          message: `Coupon needs minimum order of ${formatCurrency(minOrder)}.`,
          clearCoupon: true,
        };
      }

      const usageLimit = toPositiveInt(offerData.usageLimit, 0);
      const globalUsed = toPositiveInt(offerData.usedCount, 0);
      if (usageLimit > 0 && globalUsed >= usageLimit) {
        return {
          ok: false,
          message: "This coupon usage limit is reached.",
          clearCoupon: true,
        };
      }

      const usedByUser = toPositiveInt(usageSnap.data()?.usedCount, 0);
      const perUserLimit = toPositiveInt(offerData.perUserLimit, toPositiveInt(appliedCoupon.perUserLimit, 1) || 1) || 1;
      if (usedByUser >= perUserLimit) {
        return {
          ok: false,
          message: `This coupon can be used only ${perUserLimit} time(s) per account.`,
          clearCoupon: true,
        };
      }

      return {
        ok: true,
        offerData,
      };
    };

    setPlacingOrder(true);

    try {
      const orders = getStorageJSON("orders", []);
      const notifications = getStorageJSON("customerNotifications", []);
      const userKey = user.uid;

      const couponValidation = await validateAppliedCoupon();
      if (!couponValidation.ok) {
        if (couponValidation.clearCoupon) {
          setAppliedCoupon(null);
          setCouponMessage(couponValidation.message);
        }
        setPaymentStatus(couponValidation.message || "Coupon validation failed. Please try again.");
        setPlacingOrder(false);
        return;
      }

      const estimatedDate = resolveScheduledDeliveryDate(deliveryDateInput, new Date());
      const userAddressText = `${cleanAddress.flatNo}, ${cleanAddress.buildingNumber}, ${cleanAddress.road}, Near ${cleanAddress.landmark}, ${location} - ${cleanAddress.pinCode}`;

      try {
        await setDoc(
          doc(db, "users", user.uid),
          {
            name: cleanName,
            email: cleanEmail,
            phone: cleanPhone,
            location,
            address: userAddressText,
            addressDetails: cleanAddress,
            updatedAt: new Date(),
          },
          { merge: true }
        );
      } catch {
        // Continue checkout if profile sync is blocked by rules.
      }

      const finalizeOrder = async (payment) => {
        const order = {
          id: `ORD-${Date.now()}`,
          userId: user.uid,
          createdAt: new Date().toISOString(),
          createdAtMs: Date.now(),
          status: "pending",
          location,
          deliveryDate: estimatedDate.toISOString(),
          name: cleanName,
          email: cleanEmail,
          phone: cleanPhone,
          customer: {
            name: cleanName,
            email: cleanEmail,
            phone: cleanPhone,
          },
          address: cleanAddress,
          addressText: userAddressText,
          items: cart,
          pricing,
          coupon: appliedCoupon
            ? {
                id: appliedCoupon.id,
                code: appliedCoupon.code,
                discount: totalCouponDiscount,
                itemDiscount: couponDiscount,
                deliveryDiscount: deliveryCouponDiscount,
                freeDeliveryApplied: deliveryCouponDiscount > 0,
                offerType: appliedCoupon.offerType || "percentage",
                appliesToCategory: normalizeCouponCategory(appliedCoupon),
              }
            : null,
          payableTotal: totalPayable,
          payment,
          total: totalPayable,
        };

        await setDoc(doc(db, "orders", order.id), order);

        if (appliedCoupon?.id) {
          try {
            await runTransaction(db, async (tx) => {
              const offerRef = doc(db, "offers", appliedCoupon.id);
              const usageRef = doc(db, "offerUsage", buildCouponUsageKey(appliedCoupon.id, userKey));
              const offerSnap = await tx.get(offerRef);
              const usageSnap = await tx.get(usageRef);
              if (!offerSnap.exists()) return;

              const offerData = offerSnap.data() || {};
              const usedCount = toPositiveInt(offerData.usedCount, 0);
              const usageLimit = toPositiveInt(offerData.usageLimit, 0);
              if (usageLimit > 0 && usedCount >= usageLimit) return;

              const usedByUser = toPositiveInt(usageSnap.data()?.usedCount, 0);
              const perUserLimit = toPositiveInt(offerData.perUserLimit, toPositiveInt(appliedCoupon.perUserLimit, 1) || 1) || 1;
              if (usedByUser >= perUserLimit) return;

              tx.update(offerRef, {
                usedCount: usedCount + 1,
                updatedAt: new Date().toISOString(),
                updatedAtMs: Date.now(),
              });
              tx.set(
                usageRef,
                {
                  offerId: appliedCoupon.id,
                  userId: userKey,
                  usedCount: usedByUser + 1,
                  lastUsedAt: new Date().toISOString(),
                  lastOrderId: order.id,
                },
                { merge: true }
              );
            });
          } catch {
            // Keep order flow successful even if coupon analytics update fails.
          }
        }

        await addDoc(collection(db, "notifications"), {
          userId: user.uid,
          title: payment.method === "cod" ? "Order placed (COD)" : "Payment successful",
          message: `Order ${order.id} confirmed for ${location}. Delivery by ${estimatedDate.toLocaleDateString()}.`,
          createdAt: new Date().toISOString(),
          createdAtMs: Date.now(),
          read: false,
          orderId: order.id,
        });

        try {
          const idToken = await auth.currentUser?.getIdToken();
          await notifyOrderSuccess(
            {
              orderId: order.id,
              paymentMethod: payment.method,
              paymentVerified: Boolean(payment.verified),
              total: totalPayable,
              createdAt: order.createdAt,
              customer: {
                name: cleanName,
                email: cleanEmail,
                phone: cleanPhone,
              },
              delivery: {
                area: location,
                eta: estimatedDate.toISOString(),
              },
            },
            idToken || ""
          );
        } catch {
          // Do not block checkout on outbound notification endpoint failures.
        }

        const nextOrders = [order, ...(Array.isArray(orders) ? orders : [])];
        const nextNotifications = [
          {
            id: `N-${Date.now()}`,
            title: payment.method === "cod" ? "Order placed (COD)" : "Payment successful",
            message: `Order ${order.id} confirmed for ${location}. Delivery by ${estimatedDate.toLocaleDateString()}.`,
            createdAt: new Date().toISOString(),
            createdAtMs: Date.now(),
            read: false,
            userId: user.uid,
            orderId: order.id,
          },
          ...(Array.isArray(notifications) ? notifications : []),
        ];

        localStorage.setItem("orders", JSON.stringify(nextOrders));
        localStorage.setItem("customerNotifications", JSON.stringify(nextNotifications));
        localStorage.setItem("cart", JSON.stringify([]));

        window.dispatchEvent(new Event("cart-updated"));
        window.dispatchEvent(new Event("notifications-updated"));
        setCart([]);
        setPaymentStatus(payment.method === "cod" ? "COD order placed successfully." : payment.verified ? "Payment verified and order placed." : "Payment received. Verification pending.");

        generateInvoicePdf(order, {
          name: cleanName,
          email: cleanEmail,
          phone: cleanPhone,
        });

        setPlacingOrder(false);
        navigate("/orders");
      };

      if (paymentMethod === "cod") {
        await finalizeOrder({
          paymentId: `COD-${Date.now()}`,
          orderId: "manual-cod",
          signature: "",
          verified: false,
          method: "cod",
        });
        return;
      }

      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded) {
        setPaymentStatus("Unable to load payment gateway. Please check internet and try again.");
        setPlacingOrder(false);
        return;
      }

      const checkoutOptions = {
        key: RAZORPAY_KEY,
        amount: Math.round(totalPayable * 100),
        currency: "INR",
        name: "Shreeji Surti Swad",
        description: `Food order for ${location}`,
        image: "https://cdn-icons-png.flaticon.com/512/3075/3075977.png",
        prefill: {
          name: cleanName,
          email: cleanEmail,
          contact: cleanPhone,
        },
        notes: {
          delivery_area: location,
          address: userAddressText,
        },
        theme: {
          color: "#f97316",
        },
        handler: async (response) => {
          try {
            const verified = await verifyPaymentResponse(response);
            await finalizeOrder({
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id || "manual-order",
              signature: response.razorpay_signature || "",
              verified,
              method: "upi",
            });
          } catch (error) {
            setPaymentStatus(getFriendlyCheckoutError(error));
            setPlacingOrder(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPaymentStatus("Payment was cancelled. Your cart is still saved.");
            setPlacingOrder(false);
          },
        },
      };

      const razorpay = new window.Razorpay(checkoutOptions);
      razorpay.on("payment.failed", () => {
        setPaymentStatus("Payment failed. Please try again.");
        setPlacingOrder(false);
      });
      razorpay.open();
    } catch (error) {
      setPaymentStatus(getFriendlyCheckoutError(error));
      setPlacingOrder(false);
    }
  };

  return (
    <div className="home">
      <Navbar />

      <div className="customer-page">
        <div className="customer-page-header">
          <h2>My Cart</h2>
          <p>Review your items before checkout.</p>
        </div>

        {!cart.length ? (
          <div className="empty-state">
            <h3>Your cart is empty</h3>
            <p>Add your favorite food items to continue.</p>
            <button className="primary-action" onClick={() => navigate("/home")}>Browse Menu</button>
          </div>
        ) : (
          <div className="cart-layout">
            <div className="cart-items">
              <article className="customer-card checkout-user-card">
                <h3>Delivery Details</h3>
                <div className="customer-form compact-form">
                  <label>Name</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    maxLength={60}
                    required
                  />

                  <label>Email</label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    required
                  />

                  <label>Phone</label>
                  <input
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    maxLength={12}
                    placeholder="10-digit mobile"
                    required
                  />

                  <label>Delivery Area</label>
                  <select value={location} onChange={(e) => setLocation(normalizeDeliveryArea(e.target.value))} required>
                    {SERVICEABLE_AREAS.map((area) => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                  </select>

                  <div className="address-grid">
                    <div>
                      <label>Address Line 1</label>
                      <input
                        type="text"
                        value={address.flatNo}
                        onChange={(e) => setAddress((prev) => ({ ...prev, flatNo: e.target.value }))}
                        placeholder="Flat, house, or apartment number"
                        required
                      />
                    </div>
                    <div>
                      <label>Address Line 2</label>
                      <BuildingAutocomplete
                        value={address.buildingNumber}
                        onChange={(val) => setAddress((prev) => ({ ...prev, buildingNumber: val }))}
                        required
                      />
                    </div>
                    <div>
                      <label>Address Line 3</label>
                      <input
                        type="text"
                        value={address.road}
                        onChange={(e) => setAddress((prev) => ({ ...prev, road: e.target.value }))}
                        placeholder="Road, street, or locality"
                        required
                      />
                    </div>
                    <div>
                      <label>Landmark</label>
                      <input
                        type="text"
                        value={address.landmark}
                        onChange={(e) => setAddress((prev) => ({ ...prev, landmark: e.target.value }))}
                        placeholder="Near station, school, or shop"
                      />
                    </div>
                    <div>
                      <label>Pin Code</label>
                      <input
                        type="text"
                        value={address.pinCode}
                        onChange={(e) => setAddress((prev) => ({ ...prev, pinCode: e.target.value }))}
                        maxLength={6}
                        placeholder="400067"
                        required
                      />
                    </div>
                  </div>
                </div>
              </article>

              {cart.map((item, index) => (
                <article className="customer-card" key={`${item.id}-${item.size}-${index}`}>
                  <div className="row-between">
                    <div>
                      <h3>{item.name || "Item"}</h3>
                      <p>Size: {item.size === "1000" ? "1kg" : `${item.size}g`}</p>
                    </div>
                    <strong>{formatCurrency((Number(item.price) || 0) * (Number(item.qty) || 0))}</strong>
                  </div>

                  <div className="qty-control wide">
                    <button onClick={() => updateQty(index, (item.qty || 0) - 1)}>−</button>
                    <span>{item.qty || 0}</span>
                    <button onClick={() => updateQty(index, (item.qty || 0) + 1)}>+</button>
                  </div>
                </article>
              ))}
            </div>

            <aside className="summary-card">
              <h3>Payment Summary</h3>
              <p>Total Items: {cart.length}</p>
              <p>Subtotal: {formatCurrency(pricing.subtotal)}</p>
              <p>GST (5%): {formatCurrency(pricing.gst)}</p>
              <p>Delivery ({location}): {formatCurrency(pricing.delivery)}</p>
              <div className="coupon-apply-row">
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => setCouponInput(normalizeCouponCode(e.target.value))}
                  placeholder="Coupon code"
                />
                <button className="secondary-action" type="button" onClick={applyCoupon}>Apply</button>
              </div>
              {couponMessage && <p className="form-status">{couponMessage}</p>}
              {appliedCoupon && (
                <>
                  <p>Coupon {appliedCoupon.code}: -{formatCurrency(couponDiscount)}</p>
                  {deliveryCouponDiscount > 0 && <p>Free Delivery Coupon: -{formatCurrency(deliveryCouponDiscount)}</p>}
                  <p className="summary-savings">You saved: {formatCurrency(totalCouponDiscount)}</p>
                </>
              )}
              <h4>{formatCurrency(totalPayable)}</h4>
              <div className="coupon-apply-row" style={{ marginTop: "0.5rem" }}>
                <label htmlFor="delivery-date" style={{ fontWeight: 600, minWidth: 120 }}>
                  Delivery Date
                </label>
                <input
                  id="delivery-date"
                  type="date"
                  value={deliveryDateInput}
                  min={toDateInputValue(scheduleBounds.minDate)}
                  max={toDateInputValue(scheduleBounds.maxDate)}
                  onChange={(e) => setDeliveryDateInput(e.target.value)}
                />
              </div>

              <p>Estimated Delivery: {resolveScheduledDeliveryDate(deliveryDateInput, new Date()).toLocaleDateString()}</p>
              <p className="delivery-note">Orders before 10 PM default to next-day delivery. Orders after 10 PM default to delivery in 3 days. You can pre-schedule up to 1 week ahead.</p>

              <div className="summary-meta-row" style={{ marginTop: "0.5rem" }}>
                <label style={{ fontWeight: 600 }}>
                  Payment Method
                </label>
                <div className="payment-method-toggle" role="radiogroup" aria-label="Select payment method">
                  <button
                    type="button"
                    className={`payment-method-btn ${paymentMethod === "upi" ? "active" : ""}`}
                    onClick={() => setPaymentMethod("upi")}
                    role="radio"
                    aria-checked={paymentMethod === "upi"}
                  >
                    <span>UPI / Online</span>
                    <small>Instant confirmation</small>
                  </button>
                  {platformConfig.codEnabled && (
                    <button
                      type="button"
                      className={`payment-method-btn ${paymentMethod === "cod" ? "active" : ""}`}
                      onClick={() => setPaymentMethod("cod")}
                      role="radio"
                      aria-checked={paymentMethod === "cod"}
                    >
                      <span>Cash on Delivery</span>
                      <small>Pay when order arrives</small>
                    </button>
                  )}
                </div>
                <p className="payment-method-help">
                  {paymentMethod === "cod"
                    ? "COD selected. Keep exact amount ready for faster handover."
                    : "UPI/Online selected. You will be redirected to secure payment."}
                </p>
              </div>

              {paymentMethod === "cod" && !platformConfig.codEnabled && (
                <p className="form-status">
                  {platformConfig.codMaintenanceMessage || "Cash on Delivery is currently unavailable."}
                </p>
              )}

              {paymentStatus && <p className="form-status">{paymentStatus}</p>}

              <button className="primary-action" disabled={placingOrder} onClick={placeOrder}>
                {placingOrder ? "Processing..." : paymentMethod === "cod" ? "Place COD Order" : "Pay & Place Order"}
              </button>
            </aside>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
